import type {
  ApplicationState,
  CompetitionState,
  GradeState,
  PaymentState,
  RankingState,
} from '@concours/shared'
import { Badge, type BadgeVariant } from './Badge'

type Entry = { label: string; variant: BadgeVariant }

/** Libellés et couleurs des états métier — un seul endroit pour toute l'application. */
export const STATUS_LABELS = {
  competition: {
    draft: { label: 'Brouillon', variant: 'neutral' },
    published: { label: 'Publié', variant: 'indigo' },
    open: { label: 'Ouvert aux dépôts', variant: 'success' },
    suspended: { label: 'Suspendu', variant: 'warning' },
    closed: { label: 'Clôturé', variant: 'outline' },
    processing: { label: 'En traitement', variant: 'indigo' },
    provisional_results: { label: 'Résultats provisoires', variant: 'sahel' },
    final_results: { label: 'Résultats définitifs', variant: 'accent' },
    archived: { label: 'Archivé', variant: 'neutral' },
  } satisfies Record<CompetitionState, Entry>,
  application: {
    draft: { label: 'Brouillon', variant: 'neutral' },
    ready: { label: 'Prêt à soumettre', variant: 'accent' },
    submitted: { label: 'Soumis', variant: 'indigo' },
    under_review: { label: 'En vérification', variant: 'info' },
    to_complete: { label: 'À compléter', variant: 'sahel' },
    admissible: { label: 'Recevable', variant: 'success' },
    not_admissible: { label: 'Non recevable', variant: 'danger' },
    withdrawn: { label: 'Retiré', variant: 'outline' },
    cancelled: { label: 'Annulé', variant: 'outline' },
  } satisfies Record<ApplicationState, Entry>,
  payment: {
    not_required: { label: 'Non requis', variant: 'neutral' },
    pending: { label: 'En attente', variant: 'warning' },
    processing: { label: 'En cours', variant: 'info' },
    confirmed: { label: 'Confirmé', variant: 'success' },
    failed: { label: 'Échoué', variant: 'danger' },
    cancelled: { label: 'Annulé', variant: 'outline' },
    refunded: { label: 'Remboursé', variant: 'indigo' },
  } satisfies Record<PaymentState, Entry>,
  grade: {
    draft: { label: 'Brouillon', variant: 'neutral' },
    submitted: { label: 'Soumise', variant: 'info' },
    validated: { label: 'Validée', variant: 'success' },
    unlocked: { label: 'Déverrouillée', variant: 'warning' },
  } satisfies Record<GradeState, Entry>,
  ranking: {
    draft: { label: 'Brouillon', variant: 'neutral' },
    under_control: { label: 'En contrôle', variant: 'sahel' },
    approved: { label: 'Approuvé', variant: 'success' },
    published: { label: 'Publié', variant: 'accent' },
  } satisfies Record<RankingState, Entry>,
} as const

type Domains = typeof STATUS_LABELS
export type StatusDomain = keyof Domains

export interface StatusBadgeProps<D extends StatusDomain> {
  domain: D
  status: keyof Domains[D] & string
  className?: string
}

export function StatusBadge<D extends StatusDomain>({
  domain,
  status,
  className,
}: StatusBadgeProps<D>) {
  const entry = (STATUS_LABELS[domain] as Record<string, Entry>)[status]
  if (!entry) return <Badge className={className}>{status}</Badge>
  return (
    <Badge variant={entry.variant} dot className={className}>
      {entry.label}
    </Badge>
  )
}
